import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { deposerPiece, useActionsFacture, useBonsCommande, useFacture, type FactureSaisie } from '@/api/hooks';
import type { FactureRecue } from '@/api/types';
import { Chargement, PageHeader } from '@/components/common';
import { ChampFichier, messageErreurFichier } from '@/components/pieces-jointes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { montant } from '@/lib/utils';

type Devise = 'USD' | 'CDF' | 'EUR';
const TAUX_DEFAUT: Record<Devise, number> = { USD: 1, CDF: 0.00035, EUR: 1.08 };

interface Valeurs {
  fournisseur: string;
  numeroFacture: string;
  dateFacture: string;
  dateReception: string;
  objet: string;
  montant: number;
  tva: number;
  devise: Devise;
  tauxChange: number;
  referenceCommande: string;
  bonCommandeId: string;
}

const auj = () => new Date().toISOString().slice(0, 10);
const depuis = (f: FactureRecue): Valeurs => ({
  fournisseur: f.fournisseur,
  numeroFacture: f.numeroFacture,
  dateFacture: f.dateFacture,
  dateReception: f.dateReception,
  objet: f.objet,
  montant: f.montant,
  tva: f.tva,
  devise: f.devise as Devise,
  tauxChange: f.montant > 0 ? Math.round((f.montantUsd / f.montant) * 1_000_000) / 1_000_000 : TAUX_DEFAUT[f.devise as Devise] ?? 1,
  referenceCommande: f.referenceCommande ?? '',
  bonCommandeId: f.bonCommandeId ?? '',
});

/** Enregistrement d'une facture reçue (numéro IN-xxxxxx attribué par le système, contrôle de doublon R3 côté API). */
export function FactureFormulaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const existant = useFacture(id);
  const bons = useBonsCommande({ statut: 'AUTORISE' });
  const actions = useActionsFacture();
  // Facture numérisée (facultative à l'enregistrement, jointe juste après la création).
  const [fichier, setFichier] = useState<File | null>(null);
  const form = useForm<Valeurs>({ defaultValues: { fournisseur: '', numeroFacture: '', dateFacture: auj(), dateReception: auj(), objet: '', montant: 0, tva: 0, devise: 'USD', tauxChange: 1, referenceCommande: '', bonCommandeId: '' } });
  const valeurs = useWatch({ control: form.control });

  useEffect(() => {
    if (existant.data) form.reset(depuis(existant.data.facture));
  }, [existant.data, form]);

  // Un bon de commande autorisé préremplit le fournisseur et l'objet si la saisie est vide.
  useEffect(() => {
    const bc = bons.data?.find((b) => b.id === valeurs.bonCommandeId);
    if (!bc) return;
    if (!form.getValues('fournisseur')) form.setValue('fournisseur', bc.fournisseur);
    if (!form.getValues('objet')) form.setValue('objet', bc.objet);
    if (!form.getValues('referenceCommande') && bc.numero) form.setValue('referenceCommande', bc.numero);
  }, [valeurs.bonCommandeId, bons.data, form]);

  const devise = (valeurs.devise ?? 'USD') as Devise;
  const taux = devise === 'USD' ? 1 : Number(valeurs.tauxChange) || TAUX_DEFAUT[devise];
  const totalUsd = (Number(valeurs.montant) || 0) * taux;
  const bc = bons.data?.find((b) => b.id === valeurs.bonCommandeId);

  const enregistrer = form.handleSubmit(
    async (v) => {
      const corps: FactureSaisie = {
        fournisseur: v.fournisseur.trim(),
        numeroFacture: v.numeroFacture.trim(),
        dateFacture: v.dateFacture,
        dateReception: v.dateReception,
        objet: v.objet.trim(),
        montant: Number(v.montant) || 0,
        tva: Number(v.tva) || 0,
        devise: v.devise,
        tauxChange: v.devise === 'USD' ? 1 : Number(v.tauxChange) || TAUX_DEFAUT[v.devise],
        referenceCommande: v.referenceCommande.trim() || undefined,
        bonCommandeId: v.bonCommandeId || undefined,
      };
      if (corps.montant <= 0) return toast.error('Le montant doit être supérieur à zéro.');
      let r;
      try {
        r = id ? await actions.modifier.mutateAsync({ id, corps }) : await actions.creer.mutateAsync(corps);
      } catch (e) {
        return toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
      }
      if (!id && fichier) {
        try {
          await deposerPiece('FA', r.facture.id, fichier, 'FACTURE');
          toast.success(`Facture ${r.facture.numero} enregistrée avec sa copie numérisée.`);
        } catch (e) {
          toast.error(`Facture ${r.facture.numero} enregistrée, mais la copie n'a pas pu être jointe : ${messageErreurFichier(e)} Joignez-la depuis la page de la facture.`);
        }
      } else toast.success(id ? 'Facture modifiée.' : `Facture ${r.facture.numero} enregistrée.`);
      navigate(`/factures/${r.facture.id}`);
    },
    () => toast.error("Renseignez le fournisseur, le numéro de facture, les dates, l'objet et le montant."),
  );

  if (id && existant.isLoading) return <Chargement />;

  return (
    <form onSubmit={enregistrer} className="space-y-6">
      <PageHeader
        retour={
          <Link to={id ? `/factures/${id}` : '/factures'} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Retour
          </Link>
        }
        titre={id ? `Modifier la facture ${existant.data?.facture.numero ?? ''}` : 'Enregistrer une facture reçue'}
        sousTitre="Le numéro FA/{année}/{séquence} est attribué automatiquement ; une facture déjà enregistrée pour le même fournisseur et le même numéro est refusée (règle R3)."
        actions={
          <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
            {id ? 'Enregistrer' : 'Enregistrer la facture'}
          </Button>
        }
      />
      <Card>
        <CardHeader>
          <CardTitle>Facture</CardTitle>
          <CardDescription>Montant toutes taxes comprises ; la contre-valeur en USD est calculée avec le taux indiqué.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="fournisseur">Fournisseur</Label>
            <Input id="fournisseur" {...form.register('fournisseur', { required: true, minLength: 2 })} placeholder="Voyages Horizon SARL" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="numeroFacture">Numéro de facture du fournisseur</Label>
            <Input id="numeroFacture" {...form.register('numeroFacture', { required: true })} placeholder="FV-2026-0412" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dateFacture">Date de la facture</Label>
            <Input id="dateFacture" type="date" {...form.register('dateFacture', { required: true })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dateReception">Date de réception</Label>
            <Input id="dateReception" type="date" {...form.register('dateReception', { required: true })} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="objet">Objet</Label>
            <Input id="objet" {...form.register('objet', { required: true, minLength: 5 })} />
          </div>
          <div className="grid grid-cols-3 gap-2 md:col-span-2">
            <div className="space-y-1.5">
              <Label htmlFor="montant">Montant TTC</Label>
              <Input id="montant" type="number" min={0} step="0.01" {...form.register('montant', { required: true, valueAsNumber: true })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tva">dont TVA</Label>
              <Input id="tva" type="number" min={0} step="0.01" {...form.register('tva', { valueAsNumber: true })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="devise">Devise</Label>
              <SelectNatif id="devise" {...form.register('devise')}>
                <option value="USD">USD</option>
                <option value="CDF">CDF</option>
                <option value="EUR">EUR</option>
              </SelectNatif>
            </div>
          </div>
          {devise !== 'USD' && (
            <div className="space-y-1.5">
              <Label htmlFor="taux">Taux (1 {devise} = ? USD)</Label>
              <Input id="taux" type="number" min={0} step="0.000001" {...form.register('tauxChange', { valueAsNumber: true })} />
            </div>
          )}
          <div className="num self-end pb-2 text-sm text-muted-foreground">Contre-valeur : {montant(totalUsd, true)}</div>
          <div className="space-y-1.5">
            <Label htmlFor="bc">Bon de commande autorisé (rapprochement)</Label>
            <SelectNatif id="bc" {...form.register('bonCommandeId')}>
              <option value="">Aucun</option>
              {(bons.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.numero ?? 'BC'} · {b.fournisseur} · {montant(b.montantUsd, true)}
                </option>
              ))}
            </SelectNatif>
            {bc && <div className="text-xs text-muted-foreground">Commande : {montant(bc.montantUsd, true)} · écart {montant(totalUsd - bc.montantUsd, true)}</div>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reference">Référence du contrat ou du marché</Label>
            <Input id="reference" {...form.register('referenceCommande')} placeholder="BC/012/10/2026 ou contrat n°…" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="document">Facture numérisée (PDF ou image)</Label>
            {id ? <div className="text-sm text-muted-foreground">Les pièces se gèrent depuis la page de la facture.</div> : <ChampFichier id="document" valeur={fichier} onChange={setFichier} />}
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" asChild>
          <Link to={id ? `/factures/${id}` : '/factures'}>Abandonner</Link>
        </Button>
        <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
          {id ? 'Enregistrer' : 'Enregistrer la facture'}
        </Button>
      </div>
    </form>
  );
}
